import format from 'date-fns/format';
import {
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReportingBucketLatency, ReportingGrain } from '@eluve/api-contract';
import {
  Card,
  CardContent,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Text,
} from '@eluve/components';

import { formatSeconds } from './latency';
import { isBucketIncomplete } from './metrics';

type LatencyCell = {
  bucketStart: string;
  latency: ReportingBucketLatency;
};

/** Charted percentiles, mildest first — p100 stays table-only (outliers). */
const PERCENTILES = ['p50', 'p90', 'p95', 'p99'] as const;
type Percentile = (typeof PERCENTILES)[number];

// Severity ramp: the higher the percentile, the hotter the line (blue for
// p90 keeps it clearly apart from p95's orange)
const COLORS: Record<Percentile, string> = {
  p50: 'var(--color-bg-positive-strong-primary)',
  p90: 'var(--color-bg-info-strong-primary)',
  p95: 'var(--color-bg-warning-strong-secondary)',
  p99: 'var(--color-bg-negative-strong-primary)',
};

// Plotted values are floored at 1s (log axes cannot represent 0); the raw
// seconds ride along so the tooltip can still say 0s
type ChartRow = { bucket: string; incomplete: boolean } & Record<
  Percentile,
  number | null
> &
  Record<`${Percentile}Raw`, number | null>;

const renderTrendDot =
  (color: string) =>
  ({
    key,
    cx,
    cy,
    payload,
  }: {
    key?: string;
    cx?: number;
    cy?: number;
    payload?: ChartRow;
  }) =>
    !Number.isFinite(cx) || !Number.isFinite(cy) ? (
      <g key={key} />
    ) : (
      <circle
        key={key}
        cx={cx}
        cy={cy}
        r={3}
        fill="#fff"
        stroke={color}
        strokeWidth={2}
        strokeOpacity={payload?.incomplete ? 0.45 : 1}
      />
    );

const tooltip = ({
  active,
  label,
  payload: items,
}: TooltipProps<number, string>) => {
  const payload = items?.filter((item) => item.value != null);
  if (!payload?.length) {
    return null;
  }
  return (
    <ChartTooltipContent
      active={active}
      label={label}
      payload={payload}
      className="bg-background/80 backdrop-blur-sm"
      labelFormatter={(label, items) =>
        items?.[0]?.payload?.incomplete ? `${label} — In progress` : label
      }
      formatter={(value, name, item) => {
        const row = item?.payload as ChartRow | undefined;
        const raw = row?.[`${name as Percentile}Raw`];
        return (
          <div className="flex items-center gap-1.5">
            <span
              className="size-2 shrink-0 rounded-xs"
              style={{ backgroundColor: COLORS[name as Percentile] }}
            />
            <Text size="sm" className="tabular-nums">
              {name} {formatSeconds(raw ?? Number(value))}
            </Text>
          </div>
        );
      }}
    />
  );
};

const CHART_CONFIG: ChartConfig = Object.fromEntries(
  PERCENTILES.map((percentile) => [
    percentile,
    { label: percentile, color: COLORS[percentile] },
  ]),
);

// Round tick candidates in seconds (1s … 24h); the axis only shows the ones
// bracketing the actual data so the lines use the full plot height
const TICK_CANDIDATES = [
  1, 2, 5, 10, 20, 30, 60, 120, 300, 600, 1800, 3600, 7200, 14400, 43200, 86400,
];

const logAxisFor = (
  data: ChartRow[],
): { domain: [number, number]; ticks: number[] } => {
  const values = data.flatMap((row) =>
    PERCENTILES.map((percentile) => row[percentile]).filter(
      (value): value is number => value != null,
    ),
  );
  if (values.length === 0) {
    return { domain: [1, 60], ticks: [1, 10, 60] };
  }
  const min = Math.max(1, Math.min(...values));
  const max = Math.max(...values, 1);
  const lower = [...TICK_CANDIDATES].reverse().find((tick) => tick <= min) ?? 1;
  const upper =
    TICK_CANDIDATES.find((tick) => tick >= max) ??
    TICK_CANDIDATES[TICK_CANDIDATES.length - 1]!;
  return {
    // Half-tick of headroom below the floor: a line sitting exactly on the
    // domain minimum gets clipped against the plot's bottom edge
    domain: [lower / 2, upper],
    ticks: TICK_CANDIDATES.filter((tick) => tick >= lower && tick <= upper),
  };
};

/**
 * Fade gradients in chart pixel space. Object-bounding-box units (the SVG
 * default) would leave a perfectly flat series — e.g. a p50 pinned at the 1s
 * floor — with a zero-height box, and SVG skips gradients on a degenerate box
 * entirely, so that line would not paint at all. `offset` comes from the chart
 * via `Customized`.
 */
const FadeDefs: React.FC<{
  offset?: { left: number; width: number };
  fadeFrom: number;
  idFor: (percentile: Percentile) => string;
}> = ({ offset, fadeFrom, idFor }) => {
  if (!offset) {
    return null;
  }
  const x1 = offset.left;
  const x2 = offset.left + offset.width;
  return (
    <defs>
      {PERCENTILES.map((percentile) => (
        <linearGradient
          key={percentile}
          id={idFor(percentile)}
          gradientUnits="userSpaceOnUse"
          x1={x1}
          y1={0}
          x2={x2}
          y2={0}
        >
          <stop offset={fadeFrom} style={{ stopColor: COLORS[percentile] }} />
          <stop
            offset={fadeFrom}
            style={{ stopColor: COLORS[percentile], stopOpacity: 0.45 }}
          />
        </linearGradient>
      ))}
    </defs>
  );
};

const LatencyChartCard: React.FC<{
  title: string;
  data: ChartRow[];
}> = ({ title, data }) => {
  const { domain, ticks } = logAxisFor(data);
  // Everything after the second-to-last point fades when the newest bucket
  // is still in progress (category points are evenly spaced, so the
  // bounding-box offset is exact)
  const fadeFrom =
    data.length >= 2 && data[data.length - 1]!.incomplete
      ? (data.length - 2) / (data.length - 1)
      : null;
  const gradientId = (percentile: Percentile) =>
    `latency-fade-${title.replace(/\W+/g, '')}-${percentile}`;
  return (
    <Card>
      <CardContent className="p-4">
        <Text size="sm" color="tertiary">
          {title}
        </Text>
        <ChartContainer
          config={CHART_CONFIG}
          className="aspect-auto h-56 w-full"
        >
          <ComposedChart data={data} margin={{ top: 16, right: 16 }}>
            {fadeFrom != null && (
              <Customized
                component={FadeDefs}
                fadeFrom={fadeFrom}
                idFor={gradientId}
              />
            )}
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="bucket"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            {/* Log scale so p50 stays readable next to p99 spikes; the
                domain hugs the data (values floored at 1s — log axes
                cannot represent 0) */}
            <YAxis
              width={56}
              scale="log"
              domain={domain}
              ticks={ticks}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickFormatter={(value) => formatSeconds(Number(value))}
            />
            <ChartTooltip content={tooltip} />
            {PERCENTILES.map((percentile) => (
              <Line
                key={percentile}
                dataKey={percentile}
                type="monotone"
                stroke={
                  fadeFrom == null
                    ? COLORS[percentile]
                    : `url(#${gradientId(percentile)})`
                }
                strokeWidth={2}
                dot={renderTrendDot(COLORS[percentile])}
                connectNulls
              />
            ))}
          </ComposedChart>
        </ChartContainer>
        <div className="flex flex-wrap items-center justify-center gap-4 pt-1">
          {PERCENTILES.map((percentile) => (
            <div key={percentile} className="flex items-center gap-1.5">
              <span
                className="size-2.5 shrink-0 rounded-xs"
                style={{ backgroundColor: COLORS[percentile] }}
              />
              <Text size="sm" color="tertiary">
                {percentile}
              </Text>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Percentile trend lines per bucket for the Latencies tab: note wait
 * (appointment completion → first note) and upload delay (client done →
 * server confirmed, per-appointment max).
 */
export const LatencyTrendCharts: React.FC<{
  totals: LatencyCell[];
  grain: ReportingGrain;
  timezone: string;
}> = ({ totals, grain, timezone }) => {
  const toRows = (measure: 'noteWait' | 'uploadDelay'): ChartRow[] =>
    totals.map((total) => {
      const stats = total.latency[measure];
      const empty = stats.n === 0;
      return {
        bucket: format(
          new Date(`${total.bucketStart}T00:00:00`),
          grain === 'month' ? 'MMM yyyy' : 'MMM d',
        ),
        incomplete: isBucketIncomplete(total.bucketStart, grain, timezone),
        ...(Object.fromEntries(
          PERCENTILES.flatMap((percentile) => {
            const seconds = empty ? null : stats[percentile];
            return [
              // 1s floor: a log axis cannot represent 0
              [percentile, seconds == null ? null : Math.max(1, seconds)],
              [`${percentile}Raw`, seconds],
            ];
          }),
        ) as Record<Percentile | `${Percentile}Raw`, number | null>),
      };
    });
  return (
    <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
      <LatencyChartCard
        title="⏱️ Note wait percentiles"
        data={toRows('noteWait')}
      />
      <LatencyChartCard
        title="📤 Upload delay percentiles"
        data={toRows('uploadDelay')}
      />
    </div>
  );
};
